import { useState } from "react";
import type { AgentEvent } from "../../../lib/types";
import { cn } from "../../../lib/cn";
import { MessageContent } from "../../chat/MessageContent";
import { AlertTriangle, CheckCircle, Cpu } from "../../shared/Icon";
import { StatusBadge } from "../../shared/StatusBadge";
import { Collapse, Disclosure, MonoBlock } from "./primitives";
import { formatDuration, shortPath } from "./format";

/**
 * The non-tool blocks of the timeline: assistant narration, the session
 * divider, the terminal run summary, and a standalone tool result (one that
 * never matched a call — still surfaced, never dropped).
 */

/** Assistant prose — flows as text (no card) so narration reads distinct from
 * the boxed tool cards around it. Label only, no redundant icon. */
export function AssistantMessage({ text }: { text: string }) {
  if (!text.trim()) return null;
  return (
    <div className="px-1">
      <div className="mb-1 text-caption uppercase tracking-wide text-text-tertiary">
        assistant
      </div>
      <div className="text-body leading-relaxed text-text-primary">
        <MessageContent content={text} />
      </div>
    </div>
  );
}

/** Quiet divider marking a session start with model / permission / cwd. */
export function SessionStartBlock({ event }: { event: AgentEvent }) {
  const model = (event.payload.model as string | null) ?? "—";
  const cwd = (event.payload.cwd as string | null) ?? "";
  const perm = (event.payload.permission_mode as string | null) ?? "";
  return (
    <div className="flex items-center gap-3 px-1 py-1 text-mono text-[12px] text-text-tertiary">
      <span className="h-px flex-1 bg-border-subtle" aria-hidden="true" />
      <span className="inline-flex items-center gap-2">
        <Cpu size={13} className="text-text-quaternary" />
        <span className="text-text-secondary">{model}</span>
        {perm && <span className="text-text-quaternary">·</span>}
        {perm && <span>{perm}</span>}
        {cwd && <span className="text-text-quaternary">·</span>}
        {cwd && <span title={cwd}>{shortPath(cwd)}</span>}
      </span>
      <span className="h-px flex-1 bg-border-subtle" aria-hidden="true" />
    </div>
  );
}

/** Terminal run summary card — finished / failed, turns + duration, result. */
export function RunResultBlock({ event }: { event: AgentEvent }) {
  const isError = Boolean(event.payload.is_error);
  const subtype = String(event.payload.subtype ?? "");
  const duration = Number(event.payload.duration_ms ?? 0);
  const numTurns = Number(event.payload.num_turns ?? 0);
  const summary = String(event.payload.result ?? "");
  const color = isError ? "var(--status-error)" : "var(--status-success)";
  return (
    <div
      className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2.5"
      style={{ borderColor: color }}
    >
      <div className="flex items-center gap-2">
        <span style={{ color }} aria-hidden="true">
          {isError ? <AlertTriangle size={15} /> : <CheckCircle size={15} />}
        </span>
        <span className="text-small font-medium" style={{ color }}>
          {isError ? "Run failed" : "Run finished"}
        </span>
        {subtype && (
          <span className="text-mono text-[11px] text-text-tertiary">{subtype}</span>
        )}
        <div className="flex-1" />
        <span className="text-mono text-[11px] text-text-tertiary">
          {numTurns} turn{numTurns === 1 ? "" : "s"} · {formatDuration(duration)}
        </span>
      </div>
      {summary && (
        <div className="mt-2 text-body leading-relaxed text-text-secondary">
          <MessageContent content={summary} />
        </div>
      )}
    </div>
  );
}

/** A tool result with no matching call — kept visible as its own card. */
export function StandaloneResultBlock({ event }: { event: AgentEvent }) {
  const isError = Boolean(event.payload.is_error);
  const content = String(event.payload.content ?? "");
  const [open, setOpen] = useState(isError);
  return (
    <div
      className="group rounded-md border border-border-subtle bg-bg-surface transition-colors duration-fast ease-out hover:border-border-default"
      style={isError ? { borderColor: "var(--status-error)" } : undefined}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 px-2.5 py-1.5 text-left rounded-md",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        )}
      >
        <Disclosure open={open} />
        <span className="flex-1 truncate font-mono text-[12.5px] text-text-tertiary">
          {content.replace(/\s+/g, " ").trim().slice(0, 160) ||
            (isError ? "(error)" : "(empty)")}
        </span>
        <StatusBadge status={isError ? "error" : "success"} label="result" />
      </button>
      <Collapse open={open}>
        {open && (
          <div className="px-2.5 pb-2.5 pt-0.5">
            <MonoBlock tone={isError ? "error" : "default"}>
              {content || "(empty)"}
            </MonoBlock>
          </div>
        )}
      </Collapse>
    </div>
  );
}
